import {animate, AUTO_STYLE, state, style, transition, trigger} from '@angular/animations';

import {BBS_CATEGORY, VIDEO_CATEGORY} from './protocol';
import {SASEUNG} from './gukeo.const';
import {Word} from '../models/word.model';
import {SimpleDialect} from '../models/dialect.model';
import {SimpleWord} from '../models/simple-word.model';
import {Article} from '../models/article.model';
import {SimpleProverb} from '../models/proverb.model';


export const APP_VERSION: string = '1.7.4.0';
export const APP_BUILD: string = '010070400'; // 2자리, 3자리, 2자리, 2자리
export const APP_PLATFORM: string = "dweb";

/******************************************************************************
 * LocalStorage KEY
 *****************************************************************************/

export const LOCALSTORAGE_KEY_LIST_HELP = 'list-help';
export const LOCALSTORAGE_KEY_PITCH     = 'pitch';


/******************************************************************************
 * 링크 처리
 *****************************************************************************/

export type LinkInfo = {
  wordIds?: Array<string>;
  dialectIds?: Array<string>;
  dispTxt?: string;  // 화면에 표시된 형태
};


export enum RESULT_LINKPOPUP {
  POPUP_CLOSE,
  GOTO_LINK,
  GOTO_DETAIL,
};

export const LINK_POPUP_DELAY = 200;  // ms


/******************************************************************************
 * 목록 화면에서 목록의 타입 - 라우팅 주소에 넣는 값
 *****************************************************************************/

export enum LIST_TYPE {
  SERVER = 's',
  BOOKMARK = 'b',
  HISTORY = 'h',
}

// export type ListType = LIST_TYPE.SERVER | LIST_TYPE.BOOKMARK | LIST_TYPE.HISTORY;


/******************************************************************************
 * 검색 이력에 관련된 수치
 *****************************************************************************/

export const HISTORY_PAGE_SIZE = 10;
export const HISTORY_LIST_MAX = 100;
export const SEARCH_KEYWORD_MAX_LENGTH = 128;

/**
 * search-* 검색어 이력
 * view-* 검색 이력(=상세보기 한 이력)
 */
export const LOCALSTORAGE_PREFIX_HISTORY = 'history-';
export const HISTORY_KEY_SEARCH_TOTAL = 'search-total';
// export const HISTORY_KEY_VIEW_TOTAL = 'history-total';
export const HISTORY_KEY_SEARCH_WORD = 'search-word';
export const HISTORY_KEY_VIEW_WORD = 'view-word';
export const HISTORY_KEY_SEARCH_HOMONYM = 'search-homonym';
export const HISTORY_KEY_SEARCH_SAJASEUNGEO = 'search-saja';
export const HISTORY_KEY_VIEW_SAJASEUNGEO = 'view-saja';
export const HISTORY_KEY_SEARCH_PROVERB = 'search-proverb';
export const HISTORY_KEY_VIEW_PROVERB = 'view-proverb';
export const HISTORY_KEY_SEARCH_DIALECT = 'search-dialect';
export const HISTORY_KEY_VIEW_DIALECT = 'view-dialect';
export const HISTORY_KEY_SEARCH_SASEUNG = 'search-saseung';
export const HISTORY_KEY_VIEW_SASEUNG = 'view-saseung';
export const HISTORY_KEY_SEARCH_VIDEO = 'search-video';
export const HISTORY_KEY_VIEW_VIDEO = 'view-video';
export const HISTORY_KEY_SEARCH_BOARD = 'search-board';
export const HISTORY_KEY_SEARCH_EXAMPLE = 'search-example';
export const HISTORY_KEY_VIEW_EXAMPLE = 'view-example';


/******************************************************************************
 * 보안 인증에 관련된 값
 *****************************************************************************/

export const LOCALSTORAGE_KEY_AUTH = 'auth';



/******************************************************************************
 * 소리 재생에 관련된 값
 *****************************************************************************/

export const PLAY_VOICE_DELAY = 300;  // ms - 소리 재생이 너무 빨라서 추가한 지연 시간.
export const WORD_VOICE_REPLAY_COUNT = 3;   // 단어 연속 재생 횟수 (TODO: 나중에 옵션으로 이동?)
export const VOICE_INTERVAL = 500;         // 연속 재생 시 소리간 간격, 1000 = 1초


/******************************************************************************
 * 날자 형식
 *****************************************************************************/

export const DATE_FMT = 'y.MM.dd';


/******************************************************************************
 * UI 처리
 *****************************************************************************/

export const UI_RELOAD_HEIGHT = 700;          // 스크롤할 때, 화면이 리로드되는 높이
export const DIALECT_SEARCH_PLACEHOLDER = '곁에 있는 친숙한 우리말';

export const CONTAINER_SCROLL_DELAY = 10;     // 0.01 초 지연
export const LINK_POPUP_REOPEN_DELAY = 300;   // 팝업창이 다시 열려야 할 때, 대기 시간


export const HOMONYM_BOX_PADDING = 15;             // 화면 padding 크기
export const HOMOYNM_BOX_MIN_PADDING = 5;
export const HOMONYM_BOX_TOLERANCE = HOMONYM_BOX_PADDING * 2;  // 관용도, 이운이의어를 배치할 때 두개 열로 만들지 판단하는데 사용하는 상수
export const HOMONYM_BOX_MAX_PADDING_TOP = 50;

export const HOMONYM_RESIZE_DELAY = 100;      // 이운이의어 박스 크기 조정하기 전의 대기 시간

/**
 * 목록이 로딩되고, 팝업창을 띄울 때까지의 지연 시간.
 * 책상형에서는 팝업창을 띄울 필요 없으므로 간격을 줄인다.
 */
export const DETAIL_POPUP_DELAY = 100;

export const DIALECT_DETAIL_HEIGHT_DELTA = 8; // 사투리창 상세 팝업의 위치 보정값



const WORD_SELECT_ID = 'word_id_';
export function wordSelectId(word: SimpleWord): string {
  return WORD_SELECT_ID + word.id;
}

const DIALECT_SELECT_ID = 'dialect_id_';
export function dialectSelectId(dialect: SimpleDialect): string {
  return DIALECT_SELECT_ID + dialect.id;
}

const BOARD_SELECT_ID = 'board_id_';
export function boardSelectId(article: Article): string {
  return BOARD_SELECT_ID + article.no;
}

const PROVERB_SELECT_ID = 'proverb_id_';
export function proverbSelectedId(proverb: SimpleProverb): string {
  return PROVERB_SELECT_ID + proverb.id;
}

/******************************************************************************
 * 옵션 처리에 관련된 값
 *****************************************************************************/

export const LOCALSTORAGE_KEY_OPTION = 'option';

export enum OPTION_TYPE {
  NONE,
  START_ANIM,
  AUTO_PLAY,
  LARGE_MODE,
  SHOW_SASEUNG,
  WORD_LEVEL,
  SEUNGJO_DISP,
}

export enum SEUNGJO_DISP_TYPE {
  SYMBOL,
  NUMBER,
  COLOR,
}



/******************************************************************************
 * 게시판 UI 관련
 *****************************************************************************/

// BBS_CATEGORY와 순서가 일치해야 한다.
export const BBS_INFO: Array<{
  placeholder: string | null;
}> = [
  {
    placeholder: null,
  },
  {
    placeholder: '운영자의 말',
  },
  {
    placeholder: '베풀기',
  },
];

/******************************************************************************
 * 동영상 처리 관련
 *****************************************************************************/

export type VIDEO_CATEGORY_DATA = {
  category: VIDEO_CATEGORY;
  name: string;
  cssIdx: number;
};

/**
 * 영상 범주 목록
 * TODO: 영상 범주가 급격히 바뀐다면, 범주 정보도 서버에서 받아오도록 수정할 필요가 있다.
 */
export const VIDEO_DATA_LIST: Array<VIDEO_CATEGORY_DATA> = [
  {
    category: VIDEO_CATEGORY.DIALECT,
    name: '모두 우리말',
    cssIdx: 2,
  },
  {
    category: VIDEO_CATEGORY.COOL_EXPR,
    name: '멋지게 말하기',
    cssIdx: 3,
  },
  {
    category: VIDEO_CATEGORY.WRONG_EXPR,
    name: '잘못된 표현',
    cssIdx: 1,
  },
  {
    category: VIDEO_CATEGORY.PROVERB,
    name: '속담',
    cssIdx: 2,
  },
  {
    category: VIDEO_CATEGORY.LOAN_WORD,
    name: '외래어',
    cssIdx: 3,
  },
  {
    category: VIDEO_CATEGORY.HOMONYM,
    name: '같은 글자, 다른 소리',
    cssIdx: 1,
  },
];

export function findVideoData(category: VIDEO_CATEGORY): VIDEO_CATEGORY_DATA | undefined {
  return VIDEO_DATA_LIST.find(item => item.category === category);
}


/******************************************************************************
 * bookmark 실시간 처리
 *****************************************************************************/

export class BookmarkChange {
  constructor(
    public id: string | number,
    public bookmark: boolean,
  ) {
  }
}

/******************************************************************************
 * 화면에 단어와 성조, 장단기호, 한사, 사성을 표현하기위한 구조체
 *****************************************************************************/

export enum CHAR_TYPE {
  HANGUL,
  HANJA,
  JANGDAN,
}

export type CHAR_STRUCT = {
  char: string;

  type: CHAR_TYPE;
  seungjo?: number;
  seungjo2?: number;    // 허용되는 성조

  saseung?: SASEUNG;
  firstHanja?: boolean; // 할글 뒤에 처음 나오는 한자 (공간을 띄우기 위해서)
};

export type WORD_STRUCT = Array<CHAR_STRUCT>;

/*****************************************************************************
 * TODO: SearchParam 를 라우트 파라미터와 API 파라미터 양쪽에 사용하는 것이 문제임. 두개를 분리해야 함.
 */
export interface SearchParam {
  index?: string;
  keyword?: string;
  level?: number;
  start?: string;
  limit?: number;
  id?: string;
  serial?: number;
  page?: number;

  cat?: VIDEO_CATEGORY;  // video 에서만 사용
  story?: number;     // video 에서만 사용 (같은 이야기를 지역별로 다르게 표현할 때)
  word_id?: string;   // proverb 에서만 사용
  region?: string;    // dialect 에서 지역 지도 확대에 사용함.
  region2?: string;    // dialect-list-page 에서만 사용
  city?: string;      // dialect 에서만 사용
  bbs_cat?: BBS_CATEGORY; // board 에서만 사용
  saja?: number;      // sajaseungeo 에서만 사용. 1 = 사 자 성어
  hangul?: string;    // saseung-hanja 에서만 사용
  hanja?: string;    // saseung-hanja 에서만 사용

  wid?: string;        // word_id 와 같은 의미, 통합검색 (total, link에서만 사용)
  eid?: string;        // example_id 와 같은 의미, 통합검색 (total에서만 사용)
  did?: string;        // dialect_id 와 같은 의미, 통합검색 (total, link에서만 사용)
  pid?: string;        // proverb_id 와 같은 의미, 통합검색 (total에서만 사용)
  sid?: string;        // saseung3_id 와 같은 의미, 통합검색 (total에서만 사용)

  wids?: string;       // 표시해야 할 단어 id목록, "," 으로 id들을 구분 (link에서만 사용)
  dids?: string;       // 표시해야 할 사투리 id목록, "," 으로 id들을 구분 (link에서만 사용)

  hun?: string;       // 훈 더보기 api에서 사용

  postposition?: number;      // example 에서만 사용 (조사 ID 필터)
  pp?: number;                // example URL용 (postposition의 단축명)
  example_cat?: number;       // example URL용 (범주 ID)
}

export type WORD_PAGE_RESULT = {
  listWord: Array<Word>;    // 현재 받아온 단어 목록
  count: number;            // 전체 단어의 수
};


/******************************************************************************
 * 곰돌이 애니메이션 처리 관련
 *****************************************************************************/

export enum BEAR_ANIM {
  NORMAL,
  BLINK,
  SPEAK,
  EAR,
}

export const BEAR_BLINK_DURATION = 1.5 * 1000;  // 눈 깜빡이는 시간 (눈 깜빡이는 애니메니셩 2초, 조금 일찍 끊나게 함.)
export const BEAR_BLINK_DELAY = 1 * 1000;  // 눈 깜빡임 사이의 대기 시간: 1초

/******************************************************************************
 * Angular Component 애니메이션 정의
 *****************************************************************************/

export const triggerDetailPopup = trigger('wordDetailInOut', [
  transition(':enter', [
    style({transform: 'translateY(100%)'}),
    animate('100ms ease-in', style({transform: 'translateY(0%)'}))
  ]),
  transition(':leave', [
    animate('100ms ease-in', style({transform: 'translateY(50%)'}))
  ])
]);


const DEFAULT_DURATION = 200; // ms

export const triggerCollapse = trigger('collapse', [
  state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
  state('true', style({ height: '0', visibility: 'hidden' })),
  transition('false => true', animate(DEFAULT_DURATION + 'ms ease-in')),
  transition('true => false', animate(DEFAULT_DURATION + 'ms ease-in')),
  transition(':enter', [
    style({ height: '0', visibility: 'hidden' }),
    animate(DEFAULT_DURATION + 'ms ease-in', style({ height: AUTO_STYLE, visibility: AUTO_STYLE }))
  ]),
  transition(':leave', [
    style({ height: AUTO_STYLE, visibility: AUTO_STYLE }),
    animate(DEFAULT_DURATION + 'ms ease-in', style({ height: '0', visibility: 'hidden' }))
  ])
]);



export const SLIDE_IN_OUT_DELAY = 200;

export const triggerSlideInOut = trigger('slideInOut', [
  transition(':enter', [
    style({ height: '0px', opacity: 0 }),
    animate(SLIDE_IN_OUT_DELAY + 'ms ease-in-out', style({ height: '*', opacity: 1 }))
  ]),
  transition(':leave', [
    animate(SLIDE_IN_OUT_DELAY + 'ms ease-in-out', style({ height: '0px', opacity: 0 }))
  ])
]);


export const fadeInOut = trigger('fadeInOut', [
  // :enter는 void => * 전환과 동일합니다 (요소가 DOM에 삽입될 때)
  transition(':enter', [
    style({ opacity: 0 }), // 애니메이션 시작 상태를 설정합니다 (투명도 0)
    animate('200ms', style({ opacity: 1 })) // 200ms 동안 투명도를 1까지 부드럽게 전환합니다
  ]),
  // :leave는 * => void 전환과 동일합니다 (요소가 DOM에서 삭제될 때)
  transition(':leave', [
    style({ opacity: 1 }), // 애니메이션 시작 상태를 설정합니다 (투명도 1)
    animate('200ms', style({ opacity: 0 })) // 200ms 동안 투명도를 0까지 부드럽게 전환합니다
  ])
]);
